import { useEffect, useState } from 'react';
import { getStoredTheme, THEME_CHANGE_EVENT } from '@ui/shared/tokens/theme';

/**
 * Read-only theme swatch dot pinned to the rail's bottom
 * (desktop-shell.md §4) — "a quiet 'you are in the Green/Orange theme'
 * indicator, not a control." The switcher itself lives on Settings
 * (`ThemeSwitcher`); this dot never opens it, has no click target, and
 * carries its meaning entirely through `aria-label` (never color-only).
 *
 * Reads the currently-APPLIED theme (`getStoredTheme()`), unlike
 * `ThemeSwitcher`'s own decorative per-option swatches (which must show
 * both options at once regardless of which is active) — this dot only ever
 * shows one color, whichever theme is live right now, via
 * `var(--hue-primary-vivid)` so it always matches the active `[data-theme]`
 * without needing its own hex literal.
 *
 * The visual swatch already updates instantly on a same-render sibling
 * theme change (it is just a CSS custom property) — but `aria-label` is
 * plain state read once at render, so without a subscription it would go
 * stale until this component's next unrelated re-render/remount. Subscribes
 * to `THEME_CHANGE_EVENT` (dispatched by `applyTheme()`) so the label
 * updates the instant `ThemeSwitcher` (or any future theme control) changes
 * the theme, with no shared context/provider needed.
 */
export function ThemeIndicatorDot() {
  const [theme, setTheme] = useState(getStoredTheme());

  useEffect(() => {
    function handleThemeChange() {
      setTheme(getStoredTheme());
    }
    window.addEventListener(THEME_CHANGE_EVENT, handleThemeChange);
    return () => window.removeEventListener(THEME_CHANGE_EVENT, handleThemeChange);
  }, []);

  const label = theme === 'green' ? 'Green' : 'Orange';

  return (
    <span
      role="img"
      aria-label={`Current theme: ${label}`}
      style={{
        display: 'block',
        width: '16px',
        height: '16px',
        borderRadius: 'var(--radius-pill)',
        background: 'var(--hue-primary-vivid)',
        flex: 'none',
      }}
    />
  );
}
